import React, { useEffect, useRef, useState } from 'react';

// ============================================================================
// NumberField — the form-controls standard for every number in the app
// (BBM Context for claude/form-controls.md, "Number: A"). A real text
// field (inputMode=decimal, select-all on focus) flanked by full-height
// − / + keys that are big enough to hit. Replaces <input type="number">
// and its browser spinner arrows, which Lisa couldn't reliably hit.
//
// Value contract (kept string-shaped on purpose so the sweep from
// `onChange={e => set(k, e.target.value)}` is a one-token change):
//   - `value`: number | numeric string | '' | null | undefined
//   - `onChange(next)`: `next` is a sanitized numeric STRING ('' when
//     cleared). Parse with parseFloat / Number downstream exactly as the
//     native input's e.target.value was parsed.
// While the field is focused the typed text is shown verbatim (so "4."
// survives), and the keys step from the parsed value.
//
// Props: step (hours ½, money 5, counts 1), min (default 0; pass -Infinity
// to allow negatives), max, prefix ("$"), suffix ("hrs", "%"), decimals,
// size ('md' 40px | 'sm' 32px), align ('right' default | 'left'),
// disabled, readOnly, ariaLabel, placeholder, className (wrapper), inputClassName.
// ============================================================================

const fmt = (n, decimals) => {
    if (!Number.isFinite(n)) return '';
    if (Number.isInteger(n)) return String(n);
    return String(Number(n.toFixed(decimals)));
};

export default function NumberField({
    value, onChange, step = 1, min = 0, max = Infinity, decimals = 2,
    prefix, suffix, placeholder = '0', ariaLabel, size = 'md', align = 'right',
    disabled = false, readOnly = false, className = '', inputClassName = '', id, name, autoFocus,
    onBlur, onFocus, onKeyDown,
}) {
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState('');
    const inputRef = useRef(null);

    const parsed = parseFloat(value);
    const current = Number.isFinite(parsed) ? parsed : null;
    const shown = focused ? draft : (current === null ? '' : fmt(current, decimals));

    // Keep the (unfocused) draft in step with the parent value so an
    // outside change (rate-table auto-fill, form reset) shows up. While
    // focused the user's own text wins — "4." must survive a re-render.
    useEffect(() => { if (!focused) setDraft(current === null ? '' : fmt(current, decimals)); }, [current, decimals, focused]);

    const clamp = (n) => Math.min(max, Math.max(min, Math.round(n * 100) / 100));
    const emit = (raw) => {
        // Allow a leading minus only when negatives are allowed.
        const cleaned = raw.replace(min < 0 ? /[^0-9.\-]/g : /[^0-9.]/g, '');
        setDraft(cleaned);
        onChange?.(cleaned);
    };
    const bump = (dir) => {
        if (disabled || readOnly) return;
        const base = current === null ? 0 : current;
        const next = clamp(base + dir * step);
        const s = fmt(next, decimals);
        setDraft(s);
        onChange?.(s);
    };

    const h = size === 'sm' ? 'h-8' : 'h-10';
    const keyW = size === 'sm' ? 'w-8' : 'w-10';
    const text = size === 'sm' ? 'text-[13px]' : 'text-[15px]';
    const keyText = size === 'sm' ? 'text-[15px]' : 'text-[18px]';

    return (
        <div
            className={`flex items-stretch ${h} rounded-lg border bg-white overflow-hidden transition-colors ${
                disabled ? 'bg-slate-50 border-slate-200' : 'border-slate-200 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-500/20'
            } ${className}`}
        >
            {prefix && <span className="self-center pl-2.5 pr-0.5 font-mono text-[12px] text-slate-400 select-none">{prefix}</span>}
            <input
                ref={inputRef}
                id={id}
                name={name}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                autoFocus={autoFocus}
                value={shown}
                placeholder={placeholder}
                aria-label={ariaLabel}
                disabled={disabled}
                readOnly={readOnly}
                onChange={e => emit(e.target.value)}
                onFocus={e => { setFocused(true); setDraft(current === null ? '' : fmt(current, decimals)); e.target.select(); onFocus?.(e); }}
                onBlur={e => {
                    setFocused(false);
                    // Normalize on the way out: "4." → "4", "" stays "".
                    const n = parseFloat(draft);
                    const s = Number.isFinite(n) ? fmt(clamp(n), decimals) : '';
                    if (s !== draft) { setDraft(s); onChange?.(s); }
                    onBlur?.(e);
                }}
                onKeyDown={e => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); bump(e.shiftKey ? 10 : 1); }
                    else if (e.key === 'ArrowDown') { e.preventDefault(); bump(e.shiftKey ? -10 : -1); }
                    onKeyDown?.(e);
                }}
                className={`flex-1 min-w-0 w-full bg-transparent px-2.5 ${text} tabular-nums ${align === 'left' ? 'text-left' : 'text-right'} text-slate-800 placeholder:text-slate-400 focus:outline-none disabled:text-slate-500 disabled:cursor-not-allowed ${inputClassName}`}
            />
            {suffix && <span className="self-center pr-2.5 pl-0.5 font-mono text-[11px] text-slate-400 select-none whitespace-nowrap">{suffix}</span>}
            {!readOnly && (
                <>
                    <button
                        type="button" tabIndex={-1} disabled={disabled}
                        onClick={() => bump(-1)}
                        aria-label={`Decrease${ariaLabel ? ` ${ariaLabel}` : ''}`}
                        className={`${keyW} flex-shrink-0 border-l border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 font-semibold ${keyText} leading-none disabled:opacity-40 disabled:cursor-not-allowed`}
                    >−</button>
                    <button
                        type="button" tabIndex={-1} disabled={disabled}
                        onClick={() => bump(1)}
                        aria-label={`Increase${ariaLabel ? ` ${ariaLabel}` : ''}`}
                        className={`${keyW} flex-shrink-0 border-l border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 font-semibold ${keyText} leading-none disabled:opacity-40 disabled:cursor-not-allowed`}
                    >+</button>
                </>
            )}
        </div>
    );
}
